/*
 * @bot-written
 *
 * WARNING AND NOTICE
 * Any access, download, storage, and/or use of this source code is subject to the terms and conditions of the
 * Full Software Licence as accepted by you before being granted access to this source code and other materials,
 * the terms of which can be accessed on the Codebots website at https://codebots.com/full-software-licence. Any
 * commercial use in contravention of the terms of the Full Software Licence may be pursued by Codebots through
 * licence termination and further legal action, and be required to indemnify Codebots for any loss or damage,
 * including interest and costs. You are deemed to have accepted the terms of the Full Software Licence on any
 * access, download, storage, and/or use of this source code.
 *
 * BOT WARNING
 * This file is bot-written.
 * Any changes out side of "protected regions" will be lost next time the bot makes any changes.
 */
import { createContext } from 'react';
import { History } from 'history';
import { action, computed, observable } from 'mobx';
import { IGlobalModal } from 'Views/Components/Modal/GlobalModal';
import { ApolloClient } from '@apollo/client';
// % protected region % [Add any extra store imports here] on begin
import SmartlookService from '../Services/SmartlookService';
import alert from 'Util/ToastifyUtils';
import ProductEntity from './Entities/ProductEntity';
// % protected region % [Add any extra store imports here] end

// % protected region % [Change the group return result as needed] off begin
export interface IGroupResult {
	name: string;
	hasBackendAccess: boolean;
}
// % protected region % [Change the group return result as needed] end

// % protected region % [Change The user return result as needed] on begin
export interface IUserResult {
	type: 'user-data';
	id: string;
	userName: string;
	email: string;
	groups: IGroupResult[];
	organisationId?: string;
}

export interface ICodeDict { 
	[key: string]: string; 
}
// % protected region % [Change The user return result as needed] end

export interface IStore {
	/**
	 * The current location in the application
	 */
	appLocation: 'frontend' | 'admin';

	/**
	 * The router history object for React Router
	 */
	routerHistory: History;

	/**
	 * The client for Apollo
	 */
	apolloClient: ApolloClient<{}>;

	/**
	 * The global modal that is stored in the app and can be called imperatively
	 */
	modal: IGlobalModal;

	/**
	 * This signifies weather we are logged in or not
	 * Only ever set this value to true if there is a value set in this.token
	 */
	readonly loggedIn: boolean;

	/**
	 * The user Id of the logged-in user
	 */
	readonly userId: string | undefined;

	/**
	 * The user name of the logged in user
	 */
	readonly userName: string | undefined;

	/**
	 * The email of the current logged in user
	 */
	readonly email: string | undefined;

	/**
	 * The groups that the logged in user are a part of
	 */
	readonly userGroups: IGroupResult[];

	/**
	 * Does this user have access to the backend admin views
	 */
	readonly hasBackendAccess: boolean;

	/**
	 * Is the frontend in edit mode
	 */
	frontendEditMode: boolean;

	/**
	 * Sets the current logged in user in the store
	 * @param userResult
	 */
	setLoggedInUser(userResult: IUserResult): void;

	/**
	 * Clears the logged in user data from the store
	 */
	clearLoggedInUser(): void;

	// % protected region % [Add any extra store interface methods or properties here] on begin
	codeDict: ICodeDict;
	organisationId: string | undefined;
	sortDirectionElement: 'NONE' | 'ASCENDING' | 'DESCENDING';
	sortDirectionShutter: 'NONE' | 'ASCENDING' | 'DESCENDING';
	elementShutterEditMode: boolean;

	setCodeDict(dict: ICodeDict): void;
	sortProducts(productType: string, products: ProductEntity[]): void;
	showToastInEditMode(type: 'element' | 'shutter' | undefined): boolean;
	setSortDirectionElement(direction: 'NONE' | 'ASCENDING' | 'DESCENDING'): void;
	setsortDirectionShutter(direction: 'NONE' | 'ASCENDING' | 'DESCENDING'): void;
	// % protected region % [Add any extra store interface methods or properties here] end
}

/**
 * A global singleton store that contains a global state of data
 */
export class Store implements IStore {
	@observable
	user?: IUserResult;

	@observable
	appLocation: 'frontend' | 'admin' = 'frontend';

	routerHistory: History;

	apolloClient: ApolloClient<{}>;

	modal: IGlobalModal;

	@computed
	public get loggedIn() {
		// % protected region % [Customise the loggedIn getter here] off begin
		return this.user !== undefined;
		// % protected region % [Customise the loggedIn getter here] end
	}

	@computed
	public get userId(): string | undefined {
		// % protected region % [Customise the userId getter here] off begin
		return this.user ? this.user.id : undefined;
		// % protected region % [Customise the userId getter here] end
	};

	@computed
	public get userName(): string | undefined {
		// % protected region % [Customise the user name getter here] off begin
		return this.user?.userName;
		// % protected region % [Customise the user name getter here] end
	}

	@computed
	public get email(): string | undefined {
		// % protected region % [Customise the email getter here] off begin
		return this.user ? this.user.email : undefined;
		// % protected region % [Customise the email getter here] end
	}

	@computed
	public get userGroups(): IGroupResult[] {
		// % protected region % [Customise the userGroups getter here] off begin
		if (this.user) {
			return [...this.user.groups];
		}
		return [];
		// % protected region % [Customise the userGroups getter here] end
	};

	@computed
	public get hasBackendAccess() {
		// % protected region % [Customise the hasBackendAccess getter here] off begin
		if (this.user) {
			return this.user.groups.some(ug => ug.hasBackendAccess);
		}
		return false;
		// % protected region % [Customise the hasBackendAccess getter here] end
	};

	@observable
	public frontendEditMode = false;

	@action
	public setLoggedInUser(userResult: IUserResult) {
		// % protected region % [Customise the setLoggedInUser here] on begin
		this.user = userResult;
		SmartlookService.identify();
		// % protected region % [Customise the setLoggedInUser here] end
	}

	@action
	public clearLoggedInUser() {
		// % protected region % [Customise the clearLoggedInUser here] off begin
		this.user = undefined;
		// % protected region % [Customise the clearLoggedInUser here] end
	}

	// % protected region % [Add any extra store methods or properties here] on begin
	@computed
	public get organisationId(): string | undefined {
		return this.user ? this.user.organisationId : undefined;
	};

	@observable
	private codeToDescriptionDict: ICodeDict;
	
	@action setCodeDict(dict: ICodeDict) {
		this.codeToDescriptionDict = dict; 
	}
	
	@computed
	public get codeDict(): ICodeDict {
		return this.codeToDescriptionDict;
	}

	/**
	 * Sort direction for Elements
	 */
	 @observable
	 private sortDirElement: 'NONE' | 'ASCENDING' | 'DESCENDING' = 'NONE';
 
	 @action setSortDirectionElement(direction: 'NONE' | 'ASCENDING' | 'DESCENDING') {
		 this.sortDirElement = direction;
	 }
 
	 @computed
	 public get sortDirectionElement(): 'NONE' | 'ASCENDING' | 'DESCENDING' {
		 return this.sortDirElement;
	 }
 
	 /**
	  * Sort direction for Shutters
	  */
	 @observable
	 private sortDirShutter: 'NONE' | 'ASCENDING' | 'DESCENDING' = 'NONE';
 
	 @action setsortDirectionShutter(direction: 'NONE' | 'ASCENDING' | 'DESCENDING') {
		 this.sortDirShutter = direction;
	 }
 
	 @computed
	 public get sortDirectionShutter(): 'NONE' | 'ASCENDING' | 'DESCENDING' {
		 return this.sortDirShutter;
	 }

	 public sortProducts(productType: "ELEMENT" | "SHUTTER", products: ProductEntity[]) {
		products = products.sort((a, b) => {
			const direction = productType === "ELEMENT" ? store.sortDirectionElement : store.sortDirectionShutter;
			const directionMultiplier = direction === "ASCENDING" ? 1 : -1;

			// Compare based on either element or shutter
			let compareA, compareB;
			if (productType === "ELEMENT") {
				compareA = a.element?.elementId.toLocaleLowerCase();
				compareB = b.element?.elementId.toLocaleLowerCase();
			} else {
				compareA = a.shutter?.shutterId.toLocaleLowerCase();
				compareB = b.shutter?.shutterId.toLocaleLowerCase();
			}

			if (compareA === undefined && compareB === undefined) {
				return 0;
			}
			else if (compareA === undefined) {
				return 1 * directionMultiplier;
			}
			else if (compareB === undefined) {
				return -1 * directionMultiplier;
			}

			if (compareA < compareB) { return -1 * directionMultiplier; }
			if (compareA > compareB) { return 1 * directionMultiplier; }

			return 0;
		});
	 }

	/**
	 * Element/Shutter is being edited/added
	*/
	@observable
	public elementShutterEditMode = false;

	/**
	 * Show alert toast when elementShutterEditMode is true, return false
	 * @param type type of product
	 * @returns True when toast shown, otherwise False
	 */
	@action
	public showToastInEditMode(type: 'element' | 'shutter' | undefined): boolean {
		if (type !== undefined && this.elementShutterEditMode)
		{
			alert(`You have unsaved changes in another ${type}, please save the changes before continuing`, 'error');
			return true;
		}
		return false;
	}
	// % protected region % [Add any extra store methods or properties here] end
}

export const store: IStore = new Store();
export const StoreContext = createContext<IStore>(store);
